import { useDeferredValue, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Loader2, RefreshCw } from 'lucide-react'

import { api } from '@/api/client'
import { inputCls } from '@/components/Field'
import { cn, formatNum } from '@/lib/utils'
import { useFileDownload } from '@/lib/useFileDownload'
import { useTabField } from '@/stores/tabState'

interface StatusLine { partner: string; item: string; qty: string; unit: string }
interface StatusBlock { name: string; lines: StatusLine[] }
interface StatusSheet {
  key: 'mz' | 'main'; title: string; sheet: string; cols: number
  blocks: StatusBlock[]; count: number; line_count: number
}
interface StatusResponse { date: string; sheets: StatusSheet[] }

function today(): string {
  return new Date().toISOString().slice(0, 10)
}

const STEP = 300   // 한 번에 그리는 줄 수(업체 단위로 끊으므로 조금 넘을 수 있음)

const SHEET_LABEL: Record<StatusSheet['key'], string> = {
  mz: '엠즈(아티제)', main: '엠즈 외 합본',
}

/** 거래처 현황 — 그날 출고를 업체별로 펼친 표(고객 요청 2026-11-04). 업체 끝 줄 업장명에 음영,
 *  업체 수는 아래. 엑셀은 엠즈 2단 + 엠즈 외(마루팜·다원·허브팜·그린푸드·그린서울) 3단 두 시트. */
export function PartnerStatusTab() {
  const [date, setDate] = useTabField('partner-status', 'date', today())
  const [sheetKey, setSheetKey] = useTabField<StatusSheet['key']>('partner-status', 'sheet', 'main')
  const [filter, setFilter] = useState('')
  const { downloading, download } = useFileDownload()
  const { data, isFetching, refetch } = useQuery({
    queryKey: ['partner-status', date],
    queryFn: () => api<StatusResponse>(`/api/purchase/partner-status?date=${date}`),
    enabled: !!date,
  })
  const sheet = data?.sheets.find((s) => s.key === sheetKey)
  // 검색은 입력이 끝난 뒤 반영(useDeferredValue) — 글자 입력이 목록 다시 그리기에 막히지 않게.
  const f = useDeferredValue(filter.trim().toLowerCase())
  const filtered = useMemo(() => (sheet?.blocks ?? []).filter((b) => !f || b.name.toLowerCase().includes(f)
    || b.lines.some((l) => l.partner.toLowerCase().includes(f))), [sheet, f])
  // 긴 목록(합본 3천여 줄)은 앞 STEP 줄까지만 그리고 '더 보기'(업체 단위로 자름) — 느린 PC 버벅임 방지.
  const [limit, setLimit] = useState(STEP)
  useEffect(() => { setLimit(STEP) }, [date, sheetKey, f])
  const { blocks, hiddenLines } = useMemo(() => {
    let n = 0, i = 0
    while (i < filtered.length && n < limit) { n += filtered[i].lines.length; i++ }
    const total = filtered.reduce((s, b) => s + b.lines.length, 0)
    return { blocks: filtered.slice(0, i), hiddenLines: total - n }
  }, [filtered, limit])
  const td = 'px-2 py-0.5 border border-slate-200 text-sm'

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex items-center gap-3 flex-wrap shadow-sm mb-3">
        <label className="text-xs font-semibold text-slate-500">일자</label>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
               className={inputCls + ' w-44 tabular-nums'}/>
        <button onClick={() => refetch()} disabled={!date || isFetching}
                className="flex items-center gap-1.5 border border-slate-300 text-slate-600 hover:bg-slate-50 text-sm font-semibold rounded-lg px-3 py-2 disabled:opacity-40">
          <RefreshCw className={cn('w-4 h-4', isFetching && 'animate-spin')}/> 조회
        </button>
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
          {(['main', 'mz'] as const).map((k) => {
            const s = data?.sheets.find((x) => x.key === k)
            return (
              <button key={k} onClick={() => setSheetKey(k)}
                      className={cn('px-3 py-1.5 font-semibold',
                        sheetKey === k ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50')}>
                {SHEET_LABEL[k]}{s ? ` ${s.count}` : ''}
              </button>
            )
          })}
        </div>
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="업체·업장 검색"
               className={inputCls + ' w-44'}/>
        <span className="ml-auto text-xs text-slate-400 tabular-nums">
          {sheet ? `업체 ${sheet.count}곳 · ${formatNum(sheet.line_count)}줄` : (isFetching ? '조회 중…' : '')}
        </span>
        <button onClick={() => download(`/api/purchase/partner-status?date=${date}&download=1`, `거래처현황_${date}.xlsx`)}
                disabled={downloading || !data?.sheets.some((s) => s.count)}
                className="flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg px-3.5 py-2 disabled:opacity-40">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Download className="w-4 h-4"/>}
          엑셀 다운로드
        </button>
      </div>
      <p className="text-xs text-slate-400 mb-2">
        업체 = 일일매출 줄(표기단위 › 상위거래처 › 거래처), 엠즈는 매장. 업체 순서는 회사(마루팜 → 그린서울 → 허브팜 → 그린푸드 → 다원) 안에서
        출고번호 순입니다. 빠지는 줄은 구매장과 같습니다(선납·마이너스·268호). 엑셀은 샘플 양식대로 단을 나눕니다.
      </p>

      {sheet && !blocks.length && (
        <div className="text-center text-slate-400 py-12 text-sm">{f ? '검색 결과가 없습니다.' : '출고가 없습니다.'}</div>
      )}
      <div className="space-y-3">
        {blocks.map((b, bi) => (
          <div key={`${b.name}-${bi}`} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
            <div className="px-3 py-1.5 bg-slate-50 text-sm font-semibold text-slate-700 flex items-center gap-2">
              {b.name} <span className="text-xs font-normal text-slate-400">{b.lines.length}줄</span>
            </div>
            <table className="w-full border-collapse">
              <tbody>
                {b.lines.map((l, i) => (
                  <tr key={i}>
                    <td className={cn(td, 'w-1/3', i === b.lines.length - 1 && 'bg-amber-100')}>{l.partner}</td>
                    <td className={td}>{l.item}</td>
                    <td className={cn(td, 'w-20 text-right tabular-nums')}>{formatNum(l.qty)}</td>
                    <td className={cn(td, 'w-14 text-center')}>{l.unit}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
      {hiddenLines > 0 && (
        <div className="mt-3 py-2 text-center text-xs text-slate-500 bg-white border border-slate-200 rounded-lg">
          {hiddenLines.toLocaleString()}줄 더 있음 ·{' '}
          <button type="button" onClick={() => setLimit((n) => n + STEP)}
                  className="text-blue-600 hover:underline font-semibold">더 보기</button>
          {' · '}
          <button type="button" onClick={() => setLimit(Number.MAX_SAFE_INTEGER)}
                  className="text-blue-600 hover:underline">모두 보기</button>
          <span className="text-slate-400"> (검색하면 바로 찾을 수 있어요 · 엑셀은 전체)</span>
        </div>
      )}
    </div>
  )
}
